import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import { FILTER_GATEWAYS } from 'src/app/graphql/gateways.graphql';
import { FILTER_INSTALLATIONS } from 'src/app/graphql/installations.graphql';
import {
    PARK_COMMAND_LOGS,
    PARK_SCHEDULE_CREATE,
    PARK_SCHEDULE_DELETE,
    PARK_SCHEDULE_LOGS,
} from 'src/app/graphql/schedulings.graphql';
@Injectable({
    providedIn: 'root',
})
export class SchedulingService {
    constructor(private apollo: Apollo) {
        this.company = localStorage.getItem('lastCompanySelected')
            ? localStorage.getItem('lastCompanySelected')
            : null;
    }

    /** Armazena ID da empresa selecionada **/
    private company: string | null;

    public getInstallationsByReference(
        referenceContains: any,
        cursor: string | null = null
    ) {
        return this.apollo.query({
            query: FILTER_INSTALLATIONS,
            fetchPolicy: 'network-only',
            variables: {
                referenceContains,
                company: this.company,
                sort_dir: 'ASC',
                sort_field: 'REFERENCE',
                cursor,
                pageSize: 100,
                hasDevice: true,
            },
        });
    }

    /** Tamanho de página usado ao resolver as instalações de um concentrador.
     * O midgard pagina em 500 por padrão e aceita no máximo 1000, por isso a resolução é feita em laço com cursor. **/
    private readonly gatewayPageSize: number = 500;

    /**
     * Busca uma página das instalações vinculadas a um concentrador.
     * Use `getAllMacAddressesByGateway` para percorrer todas as páginas.
     **/
    public getInstallationsByGateway(gateway: any, cursor: string | null = null) {
        return this.apollo.query({
            query: FILTER_INSTALLATIONS,
            fetchPolicy: 'network-only',
            variables: {
                gateway,
                company: this.company,
                sort_dir: 'ASC',
                sort_field: 'REFERENCE',
                cursor,
                pageSize: this.gatewayPageSize,
                hasDevice: true,
            },
        });
    }

    /**
     * Resolve todos os MACs das instalações vinculadas a um concentrador,
     * percorrendo as páginas pelo cursor até hasNextPage ser falso.
     *
     * Sem o laço, um concentrador com mais instalações que o tamanho de página
     * teria as demais silenciosamente descartadas, e o agendamento sairia
     * incompleto sem nenhum erro.
     **/
    public async getAllMacAddressesByGateway(gateway: any): Promise<string[]> {
        /** Lista acumuladora de MACs do concentrador **/
        const macAddresses: string[] = [];
        /** Cursor da próxima página (null = primeira página) **/
        let cursor: string | null = null;
        /** Guarda contra cursor repetido pelo serviço (evita laço infinito) **/
        const visitedCursors = new Set<string>();

        while (true) {
            const response: any = await firstValueFrom(
                this.getInstallationsByGateway(gateway, cursor)
            );

            const data = response?.data?.installation;

            (data?.edges ?? []).forEach((edge: any) => {
                const mac = edge?.node?.device?.macAddress;
                if (mac) {
                    macAddresses.push(mac);
                }
            });

            const nextCursor: string | null = data?.pageInfo?.hasNextPage
                ? data?.pageInfo?.endCursor ?? null
                : null;

            /** Fim das páginas, ou cursor que já foi visitado **/
            if (!nextCursor || visitedCursors.has(nextCursor)) {
                break;
            }

            visitedCursors.add(nextCursor);
            cursor = nextCursor;
        }

        return macAddresses;
    }

    public getGatewaysByReference(reference: any) {
        return this.apollo.query({
            query: FILTER_GATEWAYS,
            fetchPolicy: 'network-only',
            variables: {
                company: this.company,
                reference,
                sort_dir: 'ASC',
                sort_field: 'REFERENCE',
            },
        });
    }

    /**
     * Cria um agendamento no park-scheduler.
     * Carrega junto ligar/desligar/dimerizar em uma única requisição.
     * @param macAddresses lista de MACs alvo; `null` = parque todo (explícito).
     *
     * Uma lista vazia é rejeitada de propósito: o park-scheduler trata a
     * ausência de MACs como "parque todo", então enviar `[]` agendaria a
     * empresa inteira em vez de não agendar nada.
     */
    public createParkSchedule(
        switchOnTime: string,
        switchOffTime: string,
        dimming: number,
        macAddresses: string[] | null,
        scheduleSlot: number = 1,
        channel: number = 1
    ) {
        /** Barreira contra o agendamento acidental do parque todo **/
        if (macAddresses !== null && macAddresses.length === 0) {
            throw new Error(
                'Seleção vazia: nenhuma instalação foi encontrada para o agendamento.'
            );
        }

        return this.apollo.mutate({
            mutation: PARK_SCHEDULE_CREATE,
            fetchPolicy: 'network-only',
            variables: {
                company: this.company,
                switchOnTime,
                switchOffTime,
                dimming,
                scheduleSlot,
                channel,
                macAddresses,
            },
        });
    }

    /** Deleta um agendamento (e, em cascata, seus command logs) no park-scheduler **/
    public deleteParkSchedule(requestId: string) {
        return this.apollo.mutate({
            mutation: PARK_SCHEDULE_DELETE,
            fetchPolicy: 'network-only',
            variables: { company: this.company, requestId },
        });
    }

    /** Lista os logs de requisição (nível requisição) do park-scheduler **/
    public getParkScheduleLogs(
        filters: {
            requestId?: string | null;
            createdBy?: string | null;
            status?: string | null;
            dateGte?: string | null;
            dateLte?: string | null;
        },
        cursor: string | null,
        pageSize: number
    ) {
        return this.apollo.query({
            query: PARK_SCHEDULE_LOGS,
            fetchPolicy: 'network-only',
            variables: {
                company: this.company,
                ...filters,
                cursor,
                pageSize,
            },
        });
    }

    /** Lista os logs de comando (nível comando) do park-scheduler **/
    public getParkCommandLogs(
        filters: {
            requestId?: string | null;
            installationId?: string | null;
            macAddresses?: string[] | null;
            serialNumber?: string | null;
            status?: string | null;
            dateGte?: string | null;
            dateLte?: string | null;
        },
        cursor: string | null,
        pageSize: number
    ) {
        return this.apollo.query({
            query: PARK_COMMAND_LOGS,
            fetchPolicy: 'network-only',
            variables: {
                company: this.company,
                ...filters,
                cursor,
                pageSize,
            },
        });
    }
    /** Tipo de separador do arquivo **/
    private separatorString: string = ';';

    public importDataFromCSV(csvText: string): Array<any> {
        /** Constante que armazena uma lista onde estão sendo separadas com base no separador
         * representa as propriedades do CSV (cabeçalho).
         * **/
        csvText = csvText?.replace(/\r/g, '');

        const propertyNames = csvText
            ?.slice(0, csvText.indexOf('\n'))
            ?.split(this.separatorString);

        /** Constante que armazena as linhas com os dados **/
        const dataRows = csvText?.slice(csvText.indexOf('\n') + 1)?.split('\n');

        /** Array para armazenar a lista de objetos **/
        let dataArray: any[] = [];

        /** Para cada linha do arquivo **/
        dataRows.forEach((row) => {
            /** Separa os valores com base no separador **/
            let values = row?.split(this.separatorString);

            /** Instancia um novo objeto abstrato **/
            let obj: any = new Object();

            /** Para cada uma das propriedades **/
            for (let index = 0; index < propertyNames.length; index++) {
                /** Pega o nome da propriedade pelo conteúdo do índice da lista **/
                const propertyName: string = propertyNames[index];

                /** Pega o valor da linha, baseado no índice da lista de valores **/
                let val: any = values[index];
                /** Seta nulo para itens vazios **/
                if (val === '') {
                    val = null;
                }
                /** Seta no objeto uma propriedade passando o valor do índice compatível **/
                obj[propertyName] = val;
            }

            /** Adiciona o objeto referente a linha de dados **/
            dataArray.push(obj);
        });

        /** Retorna uma lista de objetos referente aos dados do CSV **/
        return dataArray;
    }
}
